import { Component, Element, Prop, VNode, Watch, h } from '@stencil/core';

import * as Pipeline from '../core/utils/pipeline';
import { BaseComponent } from '../core/interfaces/BaseComponent';
import { Dictionary } from '../core/classes/Dictionary';
import { Theme } from '../core/types/globalTypes';
import { generateId } from '../core/utils/components';
import { isNullOrUndefinedOrEmpty } from 'utils/collection';

/**
 * Breadcrumbs help users keep track of the active and current navigational state.
 * @tag `<ads-breadcrumbs>`
 * @slot breadcrumbs - The breadcrumbs displayed inside the component
 * @example
 * <ads-breadcrumbs sr-label="Breadcrumbs">
 *   <ads-breadcrumb slot="breadcrumb" link="#" sr-label="Link to Text1">Text1</ads-breadcrumb>
 *   <ads-breadcrumb slot="breadcrumb" link="#" sr-label="Link to Text2">Text2</ads-breadcrumb>
 *   <ads-breadcrumb slot="breadcrumb" link="#" sr-label="Link to Text3">Text3</ads-breadcrumb>
 *   <ads-breadcrumb slot="breadcrumb" link="#" sr-label="Link to Text4" aria-current="true">Text4</ads-breadcrumb>
 * </ads-breadcrumbs>
 */
@Component({
  tag: 'ads-breadcrumbs',
  styleUrl: 'ads-breadcrumbs.scss',
  shadow: true,
})
export class AdsBreadcrumbs implements BaseComponent {
  /**
   * @hidden
   */
  public component: string;

  constructor() {
    this.el.setAttribute('id', this.identifier || generateId('ads-breadcrumbs'));
    this.component = 'ads-breadcrumbs';
    Dictionary.registerComponent(this);
  }

  /**
   * The component element
   */
  @Element() private el: HTMLAdsBreadcrumbsElement;

  /**
   * The unique identifier (optional)
   */
  @Prop({ attribute: 'id' }) identifier?: string;

  /**
   * The theme for this component instance (optional)
   * Values: "default", "dark", "light"
   */
  @Prop({ reflect: true }) theme?: Theme;

  /**
   * The value for the aria-label, to describe the purpose of the breadcrumbs component (optional).
   * Translations for the default value can be configured, if required, via the `NAVIGATION_MENU` key.
   * @default "Navigation Menu"
   */
  @Prop({ reflect: true }) srLabel: string = Dictionary.get('Navigation Menu', 'NAVIGATION_MENU');

  /**
   * The breadcrumb delimiter (optional)
   */
  @Prop({ reflect: true }) delimiter?: string;

  /**
   * Handle identifier property changes
   * @param {string} newValue The new value for "identifier"
   * @hidden
   */
  @Watch('identifier')
  handleIdentifierChange(newValue: string): void {
    this.el.setAttribute('id', newValue || generateId('ads-breadcrumbs'));
  }

  /**
   * Handle delimiter change
   * @param {string}   newValue  The new value for 'delimiter'
   * @hidden
   */
  @Watch('delimiter')
  handleDelimiterChange(newValue: string): void {
    this.setDelimiter(newValue);
  }

  private setDelimiter = (newDelimiter: string) => {
    const $breadcrumbs = this.el.querySelectorAll('ads-breadcrumb');
    $breadcrumbs.forEach(($breadcrumb) => {
      if (!isNullOrUndefinedOrEmpty(newDelimiter)) {
        $breadcrumb.setAttribute('data-delimiter', newDelimiter);
        $breadcrumb.style.setProperty('--breadcrumb-delimiter', `'${newDelimiter}'`);
      }
    });
  };

  protected componentWillLoad(): void {
    this.setDelimiter(this.delimiter);
  }

  protected componentDidLoad(): void {
    Pipeline.addComponent(this.el);
  }

  protected disconnectedCallback(): void {
    Pipeline.removeComponent(this.el);
  }

  protected render(): VNode {
    const attributes = {
      role: 'navigation',
      'aria-label': this.srLabel,
    };

    const classes = ['c-breadcrumbs'];

    return (
      <nav class={classes.join(' ')} {...attributes}>
        <slot name="breadcrumb" />
      </nav>
    ) as VNode;
  }
}
